import Immutable from 'immutable';

import {
	createModuleState,
	EntitySearchAndDetailModel,
	DefinitionUtils,
	EntityDetailModel,
	styled,
	FormDefinition as Form,
	FormItemDefinition as FormItem,
	CrossDefinition as Cross,
	FileInputDefinition as FileInput,
	TabPaneDefinition as TabPane,
	BoxDefinition as Box,
	TabDefinition as Tab,
	ModalDefinition as Modal,
	ContainerDefinition as Container,
	showMessageBox,
	REPORTED_EXCEPTION,
	webapi,
	ModuleHeader,
	ModuleHeaderItem,
	DateUtils,
	updateEntityTimestamp,
	Icon,
	ContextValidator,
	CentererDefinition as Centerer,
} from 'ps-gui';

import { getFormItemLabel, getPhrase } from '../../data/phrases';
import {
	createSearchModel, getSearchDefinition,
} from '../utils';
import { CellError, CellFiller, CellMainText, CellSubText } from '../portalresultsetmodule';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('AffirmationSubmissionSettings');

export default class AffirmationSubmissionSettingsModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setInitialized() {
		return new AffirmationSubmissionSettingsModel(this.#data.set('initialized', true));
	}

	initialize() {
		let srch = createSearchModel('/api/dq/sv_affirmation_submission_settings');
		return this.setEntitySearchAndDetail(
			new EntitySearchAndDetailModel()
				.setSearch(srch)
				.setDetail(getDetailModel())
				.setNewRowMode(EntitySearchAndDetailModel.NEW_ROW_MODE_NONE)
				.setEntityName('affirmationDistributionPeriod')
		).#setInitialized();
	}

	setEntitySearchAndDetail(model) {
		return new AffirmationSubmissionSettingsModel(this.#data.set('esad', model.getState()));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}


function fileUploadDisabled(ctx) {
	return ctx.getProperty('isNew') || ctx.getRootProperty('detail') !== ctx.getRootProperty('originalDetail');
}

async function uploadFile(files) {
	if (!files || files.length === 0) {
		return;
	}

	if (files.length > 1) {
		showMessageBox(getPhrase('selectOneFile'));
		return;
	}

	const file = files[0];
	const uploadedFile = await webapi.upload('/api/file', file);
	if (uploadedFile === REPORTED_EXCEPTION) {
		return;
	}
	return uploadedFile;
}

function getFileImporter(type) {
	return async function(ctx, value) {
		const uploadedFile = await uploadFile(value);
		console.log('uploadedFile', uploadedFile);
		if (!uploadedFile) {
			return;
		}
		const importId = await webapi.execute('/api/affirmation/import' + type, {
			fileId: uploadedFile.fileId,
			distributionPeriodId: ctx.getProperty('distributionPeriodId'),
			_pendingRequestMessage: getPhrase('importingFile').replace('$fileName', uploadedFile.originalFileName)
		});
		if (importId === REPORTED_EXCEPTION) {
			return;
		}
		const res = await webapi.execute('/api/proc/api_affirmations/consolidate' + type + 'Import', {
			importId,
			_pendingRequestMessage: getPhrase('consolidatingFile').replace('$fileName', uploadedFile.originalFileName)
		});
		if (res === REPORTED_EXCEPTION) {
			return;
		}
		ctx.setRootProperty(['tmp', 'uploadButtonsHidden'], true);
		if (res.status === 'importHasErrors') {
			showImportErrors(ctx, type, importId, uploadedFile.originalFileName);
		} else {
			await showMessageBox(getPhrase('importSuccessful').replace('$fileName', uploadedFile.originalFileName));
			updateEntityTimestamp(type);
		}
		ctx.setRootProperty(['tmp', 'uploadButtonsHidden'], false);
	};
}

function renderErrorCell(name) {
	return (cell, row, colIndex) => {
		const err = row.get('err_' + name);
		const str = row.get('str_' + name);
		return (
			<CellFiller background={err ? '#DC000034' : null} padding="8px 12px">
				<CellMainText>{cell || str || '\u0020'}</CellMainText>
				<CellError>{err ? getPhrase('cellError.' + err) : null}</CellError>
			</CellFiller>
		);
	};
}

const stgUfdCustomResultProps = {
	cellPadding: '0px',
	cellRendererMap: {
		'row_id': (cell, row, colIndex) => {
			return <CellFiller padding="8px 12px">{cell + row.get('base_row_offset')}</CellFiller>;
		},
		'title': renderErrorCell('title'),
		'original_title': renderErrorCell('original_title'),
		'distributor': renderErrorCell('distributor'),
		'country_id': renderErrorCell('country_id'),
		'attendance': renderErrorCell('attendance'),
		'release_date': renderErrorCell('release_date'),
	},
	withNewEntityButton: false,
	defaultColumnWidths: {
		row_id: '100px',
		'country_id': '100px',
		'attendance': '110px',
		'release_date': '140px'
	},
	columnFilters: {
	}
};

const stgPressdataCustomResultProps = {
	cellPadding: '0px',
	cellRendererMap: {
		'row_id': (cell, row, colIndex) => {
			return <CellFiller padding="8px 12px">{cell + row.get('base_row_offset')}</CellFiller>;
		},
		channel: renderErrorCell('channel'),
		broadcast_date: renderErrorCell('broadcast_date'),
		start_time: renderErrorCell('start_time'),
		end_time: renderErrorCell('end_time'),
		title: renderErrorCell('title'),
		original_title: renderErrorCell('original_title'),
		subtitle: renderErrorCell('subtitle'),
		year: renderErrorCell('year'),
		type: renderErrorCell('type'),
		runtime: renderErrorCell('runtime'),
	},
	withNewEntityButton: false,
	defaultColumnWidths: {
		row_id: '100px',
		'country_id': '100px',
		'attendance': '110px',
		'release_date': '140px'
	},
	columnFilters: {
	}
};

function getImportErrorDialog(type, customResultProps) {
	const viewName = 'sv_stg_' + type;

	return Modal(
		Box().setContent(
			getSearchDefinition(
				viewName,
				'/api/dq/' + viewName,
				[],
				type,
				['tmp'],
				true
			).setProps(ctx => {
				let fixedFilter = Object.assign({}, (ctx.getRootProperty(['tmp', viewName + '__params']) || EMPTY_MAP).toJS());
				return {
					fixedFilter,
					customResultProps
				};
			})
		).setWidth('calc( 100vw - 100px ) ')
			.setContentHeight('calc( 90vh - 100px ) ')
			.setHeader(ctx => getPhrase('importErrorHeader').replace('$fileName', (ctx.getRootProperty(['tmp', viewName + '__params']) || EMPTY_MAP).get('fileName')))
			.setContentOverflow('auto')
			.setRightHeaderContent(ctx => {
				return (
					<Icon
						name="delete"
						active
						onMouseDown={e => {
							ctx.setRootProperty(['tmp', viewName + '__params'], null);
							ctx.setRootProperty(['tmp', viewName], null);
							console.log(ctx.getData().toJS());
						}}
					/>
				);
			})
	).setVisible(ctx => ctx.getRootProperty(['tmp', viewName + '__params'])).setRootContainer(true);
}

function showImportErrors(ctx, type, importId, fileName) {
	const viewName = 'sv_stg_' + type;
	ctx.setRootProperty(['tmp', viewName + '__params'], EMPTY_MAP.set('import_id', importId).set('fileName', fileName));
}


let detailDefinition = Container([
	TabPane().setTabs([
		Tab('basicInfo', getPhrase('basicInfo')).setContent(
			Centerer(
				Form().setBasePath('detail').setItems([
					FormItem('distributionPeriodId', 'text').setReadOnly(true),
					Cross().setItems([
						FormItem('submissionFromDate', 'date').setMandatory(true),
						FormItem('submissionToDate', 'date').setMandatory(true),
					]).setValidator(ContextValidator((errors, ctx, itemName) => {
						const fromDate = ctx.getProperty('submissionFromDate');
						const toDate = ctx.getProperty('submissionToDate');
						errors.add(ctx.getFullPath('submissionToDate'), fromDate && toDate && DateUtils.compareDates(fromDate, toDate) >= 0 ? getPhrase('submissionToDateMustBeGreaterThanSubmissionFromDate') : null, 'from>to');
					})),
					Cross().setItems([
						FormItem('conflictResolutionFromDate', 'date'),
						FormItem('conflictResolutionToDate', 'date'),
					]).setValidator(ContextValidator((errors, ctx, itemName) => {
						const fromDate = ctx.getProperty('conflictResolutionFromDate');
						const toDate = ctx.getProperty('conflictResolutionToDate');
						errors.add(ctx.getFullPath('conflictResolutionToDate'), fromDate && toDate && DateUtils.compareDates(fromDate, toDate) >= 0 ? getPhrase('conflictResolutionToDateMustBeGreaterThanConflictResolutionFromDate') : null, 'from>to');
					})),
					Cross().setJustifyContent('right').setItems([
						FileInput('uploadUfd', getPhrase('uploadUfd'))
							.setDisabled(fileUploadDisabled)
							.setMargin('16px 0px 0px 32px')
							.onChange(getFileImporter('ufd')),
						FileInput('uploadPressdata', getPhrase('uploadPressdata'))
							.setDisabled(fileUploadDisabled)
							.setMargin('16px 0px 0px 32px')
							.onChange(getFileImporter('pressdata')),
					]).setVisible(ctx => !ctx.getRootProperty(['tmp', 'uploadButtonsHidden'])),
				])
			).setWidth('640px'),
		),
		Tab('ufd', getFormItemLabel('ufd')).setContent(
			getSearchDefinition(
				'ufd',
				'/api/dq/sv_ufd',
				[
				],
				'ufd',
				['tmp'],
				true
			).setProps(ctx => {
				return {
					fixedFilter: {
						distribution_period: ctx.getRootProperty(['detail', 'distributionPeriodId'])
					},
					customResultProps: {
						defaultColumnWidths: {
							'country': '100px',
							'attendance': '140px',
							'release_date': '120px'
						},
						columnAlign: {
							attendance: 'right',
							release_date: 'right',
						}
					},
				};
			})
		),
		Tab('pressdata', getFormItemLabel('pressdata')).setContent(
			getSearchDefinition(
				'pressdata',
				'/api/dq/sv_pressdata',
				[],
				'pressdata',
				['tmp'],
				true
			).setProps(ctx => {
				return {
					fixedFilter: {
						distribution_period: ctx.getRootProperty(['detail', 'distributionPeriodId'])
					},
					customResultProps: {
						defaultColumnWidths: {
							'year': '80px',
							'runtime': '80px',
							'broadcast_time': '160px',
							'type': '220px',
							channel: '180px'
						},
						columnAlign: {
							runtime: 'right',
							year: 'right',
						},
						cellRendererMap: pressdataCellRendererMap
					},
				};
			})
		),
	]),
	getImportErrorDialog('ufd', stgUfdCustomResultProps),
	getImportErrorDialog('pressdata', stgPressdataCustomResultProps),
]);


const pressdataCellRendererMap = Object.freeze({
	'broadcast_time': (cell, row, colIndex) => {
		return (
			<>
				<CellMainText>{DateUtils.formatDate(row.get('broadcast_date'), false)}</CellMainText>
				<CellSubText>{row.get('start_time') + ' - ' + row.get('end_time')}</CellSubText>
			</>
		);
	},
	'title': (cell, row, colIndex) => {
		return (
			<>
				<CellMainText>{cell}</CellMainText>
				<CellSubText>{row.get('subtitle')}</CellSubText>
			</>
		);
	},
});


const ActivationMessage = styled.div`
	color: ${props => props.theme.lightButtonColor};
	font-weight: 500;

	margin: 8px;
	text-align: right;
`;

let detailModel = null;

function getDetailModel() {
	if (!detailModel) {
		DefinitionUtils.setLabels(detailDefinition);

		detailModel = new EntityDetailModel()
			.setLoadUri('/api/proc/api_affirmations/getSettingsDetail')
			.setSaveUri('/api/proc/api_affirmations/saveSettings')
			.setDefinition(detailDefinition)
			.setHeader(data => {
				const detail = data.get('detail') || EMPTY_MAP;
				return (
					<ModuleHeader>
						<ModuleHeaderItem>{detail.get('id')}</ModuleHeaderItem>
					</ModuleHeader>
				);
			})
			.setEntityName('affirmationDistributionPeriod');
	}
	return detailModel;
}

